import type { JwtPayload } from './auth.types';

/**
 * Decode the payload segment of a JWT (base64url → JSON).
 * No signature verification — client-side decode is for routing/display only.
 * Throws if the token is malformed (missing payload, invalid base64, invalid JSON).
 */
export function decodeJwtPayload<T = JwtPayload>(token: string): T {
  const b64url = token.split('.')[1];
  if (!b64url) throw new Error('Invalid JWT: missing payload');
  let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  const json = decodeURIComponent(
    atob(b64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
  return JSON.parse(json) as T;
}

/**
 * Safe variant — returns null for null/undefined token or invalid JWT instead of throwing.
 */
export function tryDecodeJwtPayload<T = JwtPayload>(
  token: string | null | undefined,
): T | null {
  if (!token) return null;
  try {
    return decodeJwtPayload<T>(token);
  } catch {
    return null;
  }
}

/**
 * Returns true if token is missing, malformed, or its exp is <= now + 30s skew.
 * Missing exp (legacy tokens) is considered NOT expired for backward compat.
 */
export function isTokenExpired(token: string | null | undefined): boolean {
  if (!token) return true;
  const payload = tryDecodeJwtPayload<JwtPayload>(token);
  if (!payload) return true;
  if (typeof payload.exp !== 'number') return false;
  return payload.exp * 1000 <= Date.now() + 30_000;
}

/**
 * Returns true if token exists, decodes, and exp is > now + 30s skew.
 * Missing exp is considered valid (legacy).
 */
export function isTokenValid(token: string | null | undefined): boolean {
  return !isTokenExpired(token);
}
